import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * PayPal Subscription button — renders the official gold pill PayPal button
 * for a given LIVE plan_id. Loads the SDK once (cached across all instances)
 * with `vault=true&intent=subscription` per PayPal's subscription docs.
 *
 * On approval:
 *  - if the visitor is logged in (JWT present in localStorage), we POST the
 *    subscription ID to the backend so the center gets marked "active"
 *  - either way we redirect to /paypal/success where the customer sees a
 *    friendly confirmation (matches the Auto-return URL on the plan)
 */
let sdkLoadPromise = null;
function loadPayPalSdk(clientId) {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.paypal) return Promise.resolve(window.paypal);
  if (sdkLoadPromise) return sdkLoadPromise;
  sdkLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription`;
    s.setAttribute("data-sdk-integration-source", "button-factory");
    s.onload = () => resolve(window.paypal);
    s.onerror = () => reject(new Error("Failed to load PayPal SDK"));
    document.body.appendChild(s);
  });
  return sdkLoadPromise;
}

export default function PayPalSubscribeButton({ planId, tier }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  useEffect(() => {
    if (!clientId || !planId) return;
    let cancelled = false;
    loadPayPalSdk(clientId)
      .then((paypal) => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        paypal.Buttons({
          style: { shape: "pill", color: "gold", layout: "vertical", label: "subscribe" },
          createSubscription: (_data, actions) => actions.subscription.create({ plan_id: planId }),
          onApprove: async (data) => {
            try {
              const token = localStorage.getItem("cc_token");
              if (token) {
                await api.post("/paypal/subscription/activate", {
                  subscription_id: data.subscriptionID,
                  plan_id: planId,
                  tier,
                });
              } else {
                sessionStorage.setItem("pending_paypal_subscription_id", data.subscriptionID);
                sessionStorage.setItem("pending_paypal_tier", tier || "");
              }
            } catch { /* backend record is best-effort; PayPal is source of truth */ }
            toast.success("Subscription activated — thank you!");
            window.location.href = "/paypal/success?subscription_id=" + encodeURIComponent(data.subscriptionID);
          },
          onError: (err) => {
            console.error("PayPal button error:", err);
            setError("PayPal is having trouble right now — please try again in a moment.");
          },
        }).render(containerRef.current);
        setReady(true);
      })
      .catch((err) => setError(err.message));
    return () => { cancelled = true; };
  }, [clientId, planId, tier]);

  if (!clientId || !planId) {
    return (
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`paypal-config-missing-${tier}`}>
        Subscription button not configured yet.
      </div>
    );
  }
  if (error) {
    return <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>;
  }
  return (
    <div className="min-h-[46px]" data-testid={`paypal-button-${tier}`}>
      {!ready && (
        <div className="h-11 rounded-full bg-stone-100 flex items-center justify-center text-xs text-stone-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Loading PayPal…
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
}
